import React, { useState } from "react";
import { 
  FiPlus, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiPaperclip, 
  FiTrash2, 
  FiShield, 
  FiAward, 
  FiSliders,
  FiCornerDownRight,
  FiHelpCircle
} from "react-icons/fi";
import type { FiveWhySession, FiveWhyStep, RcaCategory6M } from "../../../../types/rca.types.js";

export type FiveWhyWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  initialSteps?: FiveWhyStep[] | undefined;
  readOnly?: boolean;
  onSave?: (session: FiveWhySession) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const CATEGORIES_6M: { code: RcaCategory6M; label: string; color: string; bg: string }[] = [
  { code: "MACHINE", label: "Machine", color: "text-blue-700", bg: "bg-blue-50 border-blue-200" },
  { code: "MAN", label: "Man (Personnel)", color: "text-purple-700", bg: "bg-purple-50 border-purple-200" },
  { code: "METHOD", label: "Method", color: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
  { code: "MATERIAL", label: "Material", color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  { code: "MEASUREMENT", label: "Measurement", color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200" },
  { code: "ENVIRONMENT", label: "Environment", color: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
];

const INITIAL_STEPS: FiveWhyStep[] = [
  {
    id: "step-1",
    whyNumber: 1,
    question: "Why did dimension go out of tolerance?",
    answer: "Machine produced out-of-tolerance parts during night shift.",
    category: "MACHINE",
    evidenceDescription: "CMM measurement report #CMM-2026-081",
    evidenceIds: ["ev-1"],
    isRootCause: false,
    confidenceLevel: 95,
  },
  {
    id: "step-2",
    whyNumber: 2,
    question: "Why did machine produce out-of-tolerance parts?",
    answer: "CNC machine axis calibration drifted by 0.08mm.",
    category: "MACHINE",
    evidenceDescription: "Calibration drift log sheet",
    evidenceIds: ["ev-2"],
    isRootCause: false,
    confidenceLevel: 90,
  },
  {
    id: "step-3",
    whyNumber: 3,
    question: "Why did axis calibration drift?",
    answer: "Calibration was not done at scheduled 30-day interval.",
    category: "METHOD",
    evidenceDescription: "Maintenance schedule checklist",
    evidenceIds: ["ev-3"],
    isRootCause: false,
    confidenceLevel: 85,
  },
  {
    id: "step-4",
    whyNumber: 4,
    question: "Why was calibration not done?",
    answer: "Calibration schedule was not triggered in ERP PM module.",
    category: "METHOD",
    evidenceDescription: "ERP Maintenance task backlog",
    evidenceIds: [],
    isRootCause: false,
    confidenceLevel: 80,
  },
  {
    id: "step-5",
    whyNumber: 5,
    question: "Why was schedule not triggered in ERP?",
    answer: "No automated system alert when preventive maintenance schedule is skipped or deferred.",
    category: "METHOD",
    evidenceDescription: "ERP PM configuration audit",
    evidenceIds: ["ev-4"],
    isRootCause: true,
    rootCauseJustification: "Systemic breakdown in ERP PM automated escalation and notification workflow for critical machine calibration.",
    confidenceLevel: 90,
  },
];

export function FiveWhyWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  initialSteps,
  readOnly = false,
  onSave,
  onRootCauseIdentified,
}: FiveWhyWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"chain" | "direct">("chain");
  const [steps, setSteps] = useState<FiveWhyStep[]>(
    initialSteps && initialSteps.length > 0 ? initialSteps : INITIAL_STEPS,
  );
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState<boolean>(true);
  const [saveToast, setSaveToast] = useState<boolean>(false);

  React.useEffect(() => {
    if (initialSteps && initialSteps.length > 0) {
      setSteps(initialSteps);
    }
  }, [initialSteps]);

  const handleUpdateStep = (stepId: string, delta: Partial<FiveWhyStep>) => {
    if (readOnly) return;
    setSteps(prev => prev.map(s => {
      if (s.id === stepId) {
        const updated = { ...s, ...delta };
        if (delta.isRootCause && updated.isRootCause) {
          onRootCauseIdentified?.(updated.answer, updated.category, updated.confidenceLevel);
        }
        return updated;
      }
      if (delta.isRootCause) {
        return { ...s, isRootCause: false };
      }
      return s;
    }));
  };

  const handleAddWhy = () => {
    if (readOnly) return;
    const nextNumber = steps.length + 1;
    const newStep: FiveWhyStep = {
      id: `step-${Date.now()}`,
      whyNumber: nextNumber,
      question: `Why did step #${nextNumber - 1} occur?`,
      answer: "",
      category: "METHOD",
      confidenceLevel: 85,
      isRootCause: false,
    };
    setSteps([...steps, newStep]);
  };

  const handleRemoveWhy = (stepId: string) => {
    if (readOnly || steps.length <= 1) return;
    const remaining = steps.filter(s => s.id !== stepId);
    const renumbered = remaining.map((s, idx) => ({ ...s, whyNumber: idx + 1 }));
    setSteps(renumbered);
  };

  const handleValidateChain = () => {
    const hasEmpty = steps.some(s => !s.answer.trim());
    if (hasEmpty) {
      setIsValidated(false);
      setValidationMessage("Every Why level must have a stated causal answer before validation.");
      return;
    }
    const hasRoot = steps.some(s => s.isRootCause);
    if (!hasRoot) {
      setIsValidated(false);
      setValidationMessage("Validation warning: No step is marked as the verified Root Cause.");
      return;
    }
    setIsValidated(true);
    setValidationMessage("Chain validated successfully: Causal logic cascade is complete and verified.");

    const rootStep = steps.find(s => s.isRootCause);
    if (rootStep) {
      onRootCauseIdentified?.(rootStep.answer, rootStep.category, rootStep.confidenceLevel);
      onSave?.({
        id: `fw-${Date.now()}`,
        rcaRecordId,
        problemStatement,
        steps,
        rootCauseIdentified: true,
        rootCauseWhyNumber: rootStep?.whyNumber ?? 5,
        rootCauseDescription: rootStep?.answer ?? "",
        confidenceScore: rootStep?.confidenceLevel || 85,
        status: "VERIFIED",
      });
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Top Method Header & Subtabs */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            5W
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              11. 5-Why Analysis Workspace
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                Linear Causal Chain
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Iterative interrogative technique used to explore the cause-and-effect relationships underlying a problem.
            </p>
          </div>
        </div>

        {/* Subtabs */}
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab("chain")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "chain" 
                  ? "bg-white text-blue-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Why Chain ({steps.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("direct")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "direct" 
                  ? "bg-white text-blue-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Direct Causes
            </button>
          </div>

          <button
            type="button"
            onClick={handleValidateChain}
            className="px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
          >
            <FiCheckCircle className="w-3.5 h-3.5" />
            Validate Chain
          </button>

          {!readOnly && onSave && (
            <button
              type="button"
              onClick={() => {
                const rootStep = steps.find(s => s.isRootCause);
                onSave({
                  id: `fw-${Date.now()}`,
                  rcaRecordId,
                  problemStatement,
                  steps,
                  rootCauseIdentified: Boolean(rootStep),
                  rootCauseWhyNumber: rootStep?.whyNumber ?? steps.length,
                  rootCauseDescription: rootStep?.answer ?? "",
                  confidenceScore: rootStep?.confidenceLevel || 85,
                  status: rootStep ? "VERIFIED" : "IN_PROGRESS",
                });
                setSaveToast(true);
                setTimeout(() => setSaveToast(false), 3500);
              }}
              className="px-3.5 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
            >
              <FiShield className="w-3.5 h-3.5" />
              Save Analysis
            </button>
          )}
        </div>
      </div>

      {saveToast && (
        <div className="px-6 py-2.5 bg-emerald-100 text-emerald-900 text-xs font-semibold flex items-center gap-2 border-b border-emerald-300">
          <FiCheckCircle className="w-4 h-4 text-emerald-700" />
          5-Why Analysis successfully saved to database.
        </div>
      )}

      {/* Problem Anchor Banner */}
      <div className="px-6 py-3 bg-blue-50/60 border-b border-blue-100 flex items-start gap-3">
        <FiAlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-xs">
          <span className="font-semibold text-blue-900">Starting Problem Statement: </span>
          <span className="text-blue-800">{problemStatement || "Product dimension out of tolerance on CNC machining line."}</span>
        </div>
      </div>

      {/* Validation Message Banner */}
      {validationMessage && (
        <div className={`px-6 py-2.5 text-xs flex items-center gap-2 border-b ${
          isValidated 
            ? "bg-emerald-50 text-emerald-800 border-emerald-200" 
            : "bg-amber-50 text-amber-800 border-amber-200"
        }`}>
          {isValidated ? <FiCheckCircle className="w-4 h-4 text-emerald-600" /> : <FiAlertCircle className="w-4 h-4 text-amber-600" />}
          <span>{validationMessage}</span>
        </div>
      )}

      {/* Main Chain Content */}
      <div className="p-6 space-y-4">
        {activeTab === "chain" ? (
          <div className="space-y-4 relative">
            {steps.map((step, idx) => (
              <div 
                key={step.id} 
                className={`relative rounded-xl border transition-all ${
                  step.isRootCause 
                    ? "border-rose-300 bg-rose-50/40 shadow-sm ring-1 ring-rose-300" 
                    : "border-slate-200 bg-white shadow-sm hover:border-slate-300"
                }`}
              >
                {/* Connector Arrow for steps > 0 */}
                {idx > 0 && (
                  <div className="absolute -top-3.5 left-8 flex items-center justify-center z-10">
                    <div className="bg-slate-100 border border-slate-300 text-slate-500 rounded-full p-0.5">
                      <FiCornerDownRight className="w-3 h-3" />
                    </div>
                  </div>
                )}

                <div className="p-4 space-y-3">
                  {/* Card Header */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs ${
                        step.isRootCause 
                          ? "bg-rose-600 text-white shadow-sm" 
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                      }`}>
                        W{step.whyNumber}
                      </span>
                      <span className="text-xs font-bold text-slate-800">
                        Why Level {step.whyNumber}
                      </span>
                      {step.isRootCause && (
                        <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-rose-100 text-rose-800 border border-rose-200 flex items-center gap-1">
                          <FiAward className="w-3 h-3 text-rose-600" />
                          Root Cause
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      {/* 6M Category Selector */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] text-slate-500">6M:</span>
                        <select
                          value={step.category}
                          disabled={readOnly}
                          onChange={(e) => handleUpdateStep(step.id, { category: e.target.value as RcaCategory6M })}
                          aria-label={`6M Category for Why level ${step.whyNumber}`}
                          className="text-xs py-1 px-2 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium focus:ring-1 focus:ring-blue-500"
                        >
                          {CATEGORIES_6M.map(cat => (
                            <option key={cat.code} value={cat.code}>{cat.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Confidence Score Slider */}
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <FiSliders className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-[11px] font-medium">{step.confidenceLevel}%</span>
                        <input
                          type="range"
                          min="50"
                          max="100"
                          step="5"
                          value={step.confidenceLevel}
                          disabled={readOnly}
                          onChange={(e) => handleUpdateStep(step.id, { confidenceLevel: Number(e.target.value) })}
                          aria-label={`Confidence level for Why level ${step.whyNumber}`}
                          className="w-16 h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>

                      {/* Root Cause Toggle */}
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={step.isRootCause}
                          disabled={readOnly}
                          onChange={(e) => handleUpdateStep(step.id, { isRootCause: e.target.checked })}
                          className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4 cursor-pointer"
                        />
                        <span className={step.isRootCause ? "text-rose-600 font-bold" : ""}>Mark Root Cause</span>
                      </label>

                      {!readOnly && steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveWhy(step.id)}
                          className="text-slate-400 hover:text-rose-600 p-1 transition-colors"
                          title="Remove this Why step"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Question & Answer Inputs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">Question:</label>
                      <input
                        type="text"
                        value={step.question}
                        disabled={readOnly}
                        onChange={(e) => handleUpdateStep(step.id, { question: e.target.value })}
                        placeholder="Why did this occur?"
                        className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-1 focus:ring-blue-500 font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">Cause / Answer:</label>
                      <input
                        type="text"
                        value={step.answer}
                        disabled={readOnly}
                        onChange={(e) => handleUpdateStep(step.id, { answer: e.target.value })}
                        placeholder="State the direct cause at this step..."
                        className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 font-medium focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Root Cause Justification (if root cause) */}
                  {step.isRootCause && (
                    <div className="mt-2 p-3 bg-rose-50 rounded-lg border border-rose-200">
                      <label className="block text-[11px] font-bold text-rose-800 mb-1">
                        Root Cause Justification & Systemic Breakdown:
                      </label>
                      <textarea
                        rows={2}
                        value={step.rootCauseJustification || ""}
                        disabled={readOnly}
                        onChange={(e) => handleUpdateStep(step.id, { rootCauseJustification: e.target.value })}
                        placeholder="Explain why this represents the systemic root cause and how addressing it prevents recurrence..."
                        className="w-full text-xs p-2 rounded-lg border border-rose-300 bg-white text-slate-900 font-medium"
                      />
                    </div>
                  )}

                  {/* Evidence Attachment & Meta Footer */}
                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      <FiPaperclip className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[11px]">Evidence:</span>
                      <input
                        type="text"
                        value={step.evidenceDescription || ""}
                        disabled={readOnly}
                        onChange={(e) => handleUpdateStep(step.id, { evidenceDescription: e.target.value })}
                        placeholder="Attach reference (e.g. CMM Report, Maintenance Log)"
                        className="text-xs py-0.5 px-2 rounded-lg border border-slate-200 bg-transparent text-slate-700 w-64 focus:w-80 transition-all"
                      />
                    </div>
                    <span className="text-[11px] text-slate-400">
                      Step {step.whyNumber} of {steps.length}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Bottom Add Step Action */}
            {!readOnly && (
              <div className="flex items-center justify-center pt-2">
                <button
                  type="button"
                  onClick={handleAddWhy}
                  className="px-4 py-2 border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/50 text-slate-600 hover:text-blue-600 rounded-xl text-xs font-semibold flex items-center gap-2 transition-all"
                >
                  <FiPlus className="w-4 h-4" />
                  Add Why Step ({steps.length + 1})
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Direct Causes Table Tab */
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border border-slate-200 rounded-lg">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3">Level</th>
                  <th className="py-2.5 px-3">Question</th>
                  <th className="py-2.5 px-3">Answer / Cause</th>
                  <th className="py-2.5 px-3">6M Category</th>
                  <th className="py-2.5 px-3">Confidence</th>
                  <th className="py-2.5 px-3">Root Cause?</th>
                  <th className="py-2.5 px-3">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {steps.map(s => (
                  <tr key={s.id} className={s.isRootCause ? "bg-rose-50 font-medium" : ""}>
                    <td className="py-2.5 px-3 font-bold">Why #{s.whyNumber}</td>
                    <td className="py-2.5 px-3 text-slate-600">{s.question}</td>
                    <td className="py-2.5 px-3 text-slate-900 font-medium">{s.answer}</td>
                    <td className="py-2.5 px-3">
                      <span className="px-2 py-0.5 rounded text-[11px] bg-slate-100 text-slate-700 border border-slate-200 font-medium">
                        {s.category}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">{s.confidenceLevel}%</td>
                    <td className="py-2.5 px-3">
                      {s.isRootCause ? (
                        <span className="text-rose-600 font-bold flex items-center gap-1">
                          <FiCheckCircle className="w-3.5 h-3.5" /> YES
                        </span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-slate-500">{s.evidenceDescription || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
